import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';
import { animated } from 'react-spring';
import { useSwipeable } from 'react-swipeable';
import styled from 'styled-components';

import TypedText from '~/shared/components/TypedText';
import { Button, Dropdown } from '~/techstyle-shared/react-components';

const SelectorWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto;
  padding: ${({ theme: { sizes } }) => sizes.xxxs}px;
  background: ${({ theme: { colors } }) => colors.backgroundDisabledLight};
  border-radius: ${({ theme: { sizes } }) => sizes.xxs}px;
`;

const DropdownButton = styled(Dropdown.Button)`
  display: flex;
  justify-content: space-between;
  line-height: 1;
  border: 1px solid ${({ theme }) => theme.colors.palette.black};
  padding: ${({ theme: { sizes } }) => sizes.xs}px;
`;

const Chevron = styled(FaChevronDown)`
  transform: rotate(${({ $direction }) => ($direction === 'up' ? 180 : 0)}deg);
  transform-origin: 50% 50%;
`;

const SelectButton = animated(styled(Button).attrs({
  variant: 'unstyled',
})`
  width: 100%;
  border-radius: ${({ theme: { sizes } }) => sizes.xxs}px;
  padding: 0 ${({ theme: { sizes } }) => sizes.xxs}px;
  background: transparent;
  transition: background 0.12s 0.08s ease-in-out;
  & > span h1 {
    color: ${({ theme: { colors } }) => colors.palette.black};
  }
  & > span:before {
    content: '';
    user-select: none;
    pointer-events: none;
    ${({ $tabId }) => ($tabId === '1' ? `display: none;` : `display: block;`)}
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: ${({ theme: { sizes } }) => sizes.xxs}px;
    background: transparent;
    opacity: 1;
    transform: translateX(
      ${({ $isSelected, $tab }) => {
        if (!$isSelected && $tab === '1') {
          return '100%';
        } else if (!$isSelected && $tab === '2') {
          return '-100%';
        } else {
          return '0';
        }
      }}
    );
    transition:
      background 0.16s ease-in-out,
      opacity 0.16s ease-in-out,
      padding 0.16s ease-in-out,
      transform 0.16s ease-in-out;
  }
  ${({ theme: { colors, sizes }, $isSelected }) =>
    $isSelected &&
    `
      padding: ${sizes.xxs}px ${sizes.md}px;
      background: ${colors.palette.black};
      & > span h1 {
        color: ${colors.palette.white};
      }
      & > span:before {
        opacity: 0;
        background: ${colors.palette.white};
        transform: translateX(0);
      }
  `}
`);

const Placeholder = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SelectButtonText = styled(TypedText)`
  ${({ theme: { typography } }) => typography.variants.formLabelEmphasized}
  font-family: ${({ theme: { fontStacks } }) => fontStacks.baseFamily};
`;

function SelectorDropdownButton({ children }) {
  const [dropdownState] = Dropdown.useDropdownContext();

  return (
    <DropdownButton>
      {children}
      <Chevron $direction={dropdownState.isOpened ? 'up' : 'down'} />
    </DropdownButton>
  );
}

SelectorDropdownButton.propTypes = {
  children: PropTypes.node,
};

export default function SlidingSelector({
  className,
  onOptionSelect,
  selectedTabId: selectedTabFromProps,
  selectionOptions,
}) {
  const selectedTabId = selectedTabFromProps || selectionOptions[0].tab;

  const handleSelection = useCallback(
    (tabId) => {
      if (selectedTabId !== tabId) {
        onOptionSelect(tabId);
      }
    },
    [selectedTabId, onOptionSelect]
  );

  const swipeHandlers = useSwipeable({
    onSwipedRight: () =>
      handleSelection(
        selectionOptions[selectedTabId % selectionOptions.length].tab
      ),
    onSwipedLeft: () =>
      handleSelection(
        selectionOptions[selectedTabId % selectionOptions.length].tab
      ),
  });

  return (
    <SelectorWrapper
      className={className}
      data-autotag="sliding-selector"
      {...swipeHandlers}
    >
      {selectionOptions.length === 1 && <Placeholder>&#8212;</Placeholder>}
      {selectionOptions.map((option) => {
        const { tab, content } = option;
        return (
          <SelectButton
            key={tab}
            onClick={() => handleSelection(tab)}
            $isSelected={tab === selectedTabId}
            $tab={tab}
          >
            <SelectButtonText type="header" size="14med">
              {content}
            </SelectButtonText>
          </SelectButton>
        );
      })}
    </SelectorWrapper>
  );
}

SlidingSelector.propTypes = {
  className: PropTypes.string,
  /**
   * A callback function called when the selected option changes, with
   * the tabId of that option.
   */
  onOptionSelect: PropTypes.func,
  selectedTabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Array of options to choose from. Used for both the Dropdown on mobile, and
   * the desktop option buttons.
   */
  selectionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The url params value for "tab". Used to sync the selection option with
       * the corresponding ProductBrowser.
       */
      tab: PropTypes.number,
      /**
       * Most likely a FormattedMessage. Used for the DropdownListItem and
       * desktop option buttons.
       */
      content: PropTypes.any,
    })
  ),
};
